import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import PdfViewerDialog from '../components/PdfViewerDialog';
import { guessMimeType, setPdfPreviewHandler } from '../utils/filePreview';

export interface PdfViewOptions {
  enableAnnotations?: boolean;
  onSaveAnnotatedPdf?: (blob: Blob) => Promise<void>;
  title?: string;
}

interface PdfViewerContextType {
  viewPdf: (blob: Blob, filename: string, mimeType?: string, options?: PdfViewOptions) => void;
}

const PdfViewerContext = createContext<PdfViewerContextType | undefined>(undefined);

interface PdfViewerProviderProps {
  children: ReactNode;
}

export const PdfViewerProvider: React.FC<PdfViewerProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [filename, setFilename] = useState('');
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [enableAnnotations, setEnableAnnotations] = useState(false);
  const [onSaveAnnotatedPdf, setOnSaveAnnotatedPdf] = useState<
    ((blob: Blob) => Promise<void>) | undefined
  >(undefined);

  const closeViewer = useCallback(() => {
    setOpen(false);
    setSrc((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return null;
    });
    setFilename('');
    setTitle(undefined);
    setEnableAnnotations(false);
    setOnSaveAnnotatedPdf(undefined);
  }, []);

  const viewPdf = useCallback((blob: Blob, name: string, mimeType?: string, options?: PdfViewOptions) => {
    const type = guessMimeType(name, mimeType || blob.type);
    const fileBlob = blob.type === type ? blob : new Blob([blob], { type });
    const url = URL.createObjectURL(fileBlob);

    setSrc((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return url;
    });
    setFilename(name);
    setTitle(options?.title);
    setEnableAnnotations(Boolean(options?.enableAnnotations));
    // Wrap in function form so React doesn't treat the callback as a state updater
    setOnSaveAnnotatedPdf(() => options?.onSaveAnnotatedPdf);
    setOpen(true);
  }, []);

  useEffect(() => {
    setPdfPreviewHandler((blob, name, mimeType) => {
      viewPdf(blob, name, mimeType);
    });
    return () => setPdfPreviewHandler(null);
  }, [viewPdf]);

  return (
    <PdfViewerContext.Provider value={{ viewPdf }}>
      {children}
      <PdfViewerDialog
        open={open}
        src={src}
        filename={filename}
        title={title}
        enableAnnotations={enableAnnotations}
        onSaveAnnotatedPdf={onSaveAnnotatedPdf}
        onClose={closeViewer}
      />
    </PdfViewerContext.Provider>
  );
};

export const usePdfViewer = (): PdfViewerContextType => {
  const context = useContext(PdfViewerContext);
  if (!context) {
    throw new Error('usePdfViewer must be used within a PdfViewerProvider');
  }
  return context;
};
